import { MouseEvent, useCallback } from 'react';
import cn from 'classnames';
import copy from 'clipboard-copy';

import * as Modal from 'app/components/common/modal/Modal';
import Analytics from 'app/lib/Analytics/Analytics';

import { useDispatcher, useSelector } from 'app/store/utils/redux/hooks';
import { useAppContext } from 'app/lib/AppContext';

import styles from './Share.module.css';

import { ReactComponent as MailIcon } from './mail.svg';
import { ReactComponent as FacebookIcon } from './facebook.svg';
import { ReactComponent as WhatsappIcon } from './whatsapp.svg';
import { ReactComponent as TwitterIcon } from './twitter.svg';
import { ReactComponent as ViberIcon } from './viber.svg';

type ShareProps = {};

const Share = (props: ShareProps) => {
  const { bowser, location } = useAppContext();
  const path = location.pathname.replace(/^\//, '');

  const facebookDefault = `fb://faceweb/f?href=https://m.facebook.com/sharer.php?u=https://app.cardsnacks.com/${path}`;
  const facebookAndroid = `https://www.facebook.com/sharer/sharer.php?u=https://app.cardsnacks.com/${path}`;
  const facebookLink = String(bowser?.os).toLowerCase() === 'android' ? facebookAndroid : facebookDefault;

  const link = `https://app.mobigram.me/${path}`;
  const actions = useDispatcher();

  const isOpen = useSelector((state) => state.share.open);

  const handleClose = useCallback(() => {
    actions.share.setOpenShare(false);
  }, [actions]);

  const handleShare = useCallback((target: string) => {
    Analytics.track().tappedShare(target);
    handleClose();
  }, [handleClose]);

  const handleShareCopy = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    copy(link).then(() => {
      actions.toast.showToast('copied', 1000);
      handleShare('CopyLink');
    });
  }, [handleShare, link, actions.toast]);

  return (
    <Modal.Item visible={isOpen} onOutsideClick={handleClose}>
      <div className={styles.wrapper}>
        <div className={cn(styles.wrapperBody, styles.body)}>
          <div className={cn(styles.buttons)}>
            <a
              className={cn(styles.button, styles.mail)}
              onClick={() => handleShare('Mail')}
              href={`mailto:?subject=CardSnacks&amp;body=https://app.cardsnacks.com/${path}`}
            >
              <MailIcon />
            </a>
            <a
              className={cn(styles.button, styles.facebook)}
              onClick={() => handleShare('Facebook')}
              data-href-android={`fb://faceweb/f?href=https://m.facebook.com/sharer.php?u=https://app.cardsnacks.com/${path}`}
              href={facebookLink}
            >
              <FacebookIcon />
            </a>
            <a
              className={cn(styles.button, styles.whatsapp)}
              onClick={() => handleShare('WhatsApp')}
              href={`whatsapp://send?text=https://app.cardsnacks.com/${path}`}
            >
              <WhatsappIcon />
            </a>
            <a
              className={cn(styles.button, styles.twitter)}
              onClick={() => handleShare('Twitter')}
              href={`twitter://post?text=${encodeURIComponent(`Check out this #CardSnacks ecard I was sent! @Cardsnacks #Ecard https://app.cardsnacks.com/${path}`)}`}
            >
              <TwitterIcon />
            </a>
            <a
              className={cn(styles.button, styles.viber)}
              onClick={() => handleShare('Viber')}
              href={`viber://forward?text=https://app.cardsnacks.com/${path}`}
            >
              <ViberIcon />
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <a
              className={cn(styles.button, styles.copy)}
              onClick={handleShareCopy}
            >
              <span>
                copy
                <br />
                link
              </span>
            </a>
          </div>
        </div>
      </div>
    </Modal.Item>
  );
};

export default Share;
